import { Box, Select as NBSelect, useTheme } from 'native-base'
import { forwardRef, MutableRefObject } from 'react'
import { StyleSheet } from 'react-native'

import type { SelectProps } from '.'
import { Icon } from '../Icon'

import { hex2rgba } from '~utils'

const DROPDOWN_ARROW_SIZE = 22

export const Select = forwardRef<typeof NBSelect, SelectProps>(
  ({ isError = false, values, isFocused, isDisabled, ...props }, ref) => {
    const { colors } = useTheme()

    return (
      <NBSelect
        {...props}
        isDisabled={isDisabled}
        isFocused={!!isFocused}
        bgColor={isFocused ? hex2rgba(colors.primary['400'], 0.05) : 'white'}
        borderColor={isError ? 'error.600' : isFocused ? 'actionBlue' : 'muted.200'}
        borderWidth={isFocused ? 2 : 1}
        borderRadius="xl"
        minH="12"
        px="4"
        py="3"
        fontSize="md"
        fontWeight="500"
        color="gray.800"
        placeholderTextColor="gray.400"
        ref={(ref as MutableRefObject<typeof NBSelect>) || undefined}
        selectedValue={props.selectedValue || ''}
        _selectedItem={{
          endIcon: <Icon size={20} style={styles.checkIcon} name="check-line" color="actionBlue" />,
          bgColor: hex2rgba(colors.primary['400'], 0.12),
          borderRadius: 'xl',
          mx: 3,
          my: 1.5,
          borderLeftWidth: 3,
          borderLeftColor: 'actionBlue',
          _text: { fontWeight: '600', color: 'gray.800' },
        }}
        _actionSheetContent={{
          bgColor: 'white',
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24,
          shadow: 7,
          paddingBottom: 24,
          maxHeight: '70%',
        }}
        _actionSheetBody={{
          bgColor: 'transparent',
          py: 2,
          px: 2,
          maxHeight: '100%',
          showsVerticalScrollIndicator: false,
        }}
        _item={{
          py: 3.5,
          px: 4,
          mx: 2,
          my: 1,
          borderRadius: 'xl',
          borderWidth: 0,
          _text: {
            fontSize: 'md',
            fontWeight: '500',
            color: 'gray.700',
          },
          _hover: {
            bgColor: hex2rgba(colors.primary['400'], 0.06),
          },
          _pressed: {
            bgColor: hex2rgba(colors.primary['400'], 0.12),
          },
        }}
        dropdownIcon={
          <Box alignItems="center" justifyContent="center" pr="2" pl="1">
            <Icon
              size={DROPDOWN_ARROW_SIZE}
              name="arrow-down-s-line"
              color={isFocused ? 'actionBlue' : isDisabled ? 'gray.400' : 'gray.600'}
              style={styles.dropdownIcon}
            />
          </Box>
        }
      >
        {/* Custom placeholder option */}
        <NBSelect.Item
          label={props.placeholder || 'Select an option'}
          value=""
          key="placeholder"
          _text={{
            fontSize: 'md',
            fontWeight: '500',
            color: 'gray.400',
            fontStyle: 'italic',
          }}
          _hover={{ bgColor: 'gray.50' }}
          _pressed={{ bgColor: 'gray.100' }}
        />
        {values?.map((el, i) => (
          <NBSelect.Item
            label={el.label.charAt(0).toUpperCase() + el.label.slice(1)}
            value={el.value}
            key={`${el.value}-${i}`}
          />
        ))}
      </NBSelect>
    )
  }
)

const styles = StyleSheet.create({
  checkIcon: {
    alignSelf: 'center',
  },
  dropdownIcon: {
    alignSelf: 'center',
  },
})
